import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import projectData from './projectData.ts';
import PdfSlideshow from './PdfSlideshow';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const returnToProjects = () => {
    navigate('/');
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const projectsSection = document.getElementById('projects');
      if (projectsSection) {
        projectsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // Find the project by ID
  const project = projectData[id];
  
  if (!project) {
    return (
      <div className="project-detail-page">
        <div className="container">
          <h1>Project Not Found</h1>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      <div className="container">
        <div className="project-header">
          <div className="header-content">
            <div className="header-left">
              <div className="project-title-row">
                <h1 className="project-title">{project.title}</h1>
                {project.inProgress && (
                  <span className="status-badge in-progress">In Progress</span>
                )}
              </div>
              <p className="project-description">{project.description}</p>
              
              {project.teamMembers && project.teamMembers.length > 0 && (
                <div className="team-members">
                  <p className="team-members-label">Team Members:</p>
                  <div className="team-members-list">
                    {project.teamMembers.map((member, index) => (
                      <span key={index} className="team-member">
                        {member}
                        {index < project.teamMembers.length - 1 && <span className="team-member-separator"> • </span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="tools-used-header">
                <div className="tools-list">
                  {project.tools.map((tool, index) => (
                    <span key={index} className="tool-tag">{tool}</span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="header-right">
              <div className="main-image">
                <img src={project.mainImage} alt={project.title} />
              </div>
            </div>
          </div>
        </div>

        <div className="project-content">
          <div className="project-story">
            {project.story.map((storyItem, index) => {
              if (storyItem.pdf) {
                return (
                  <div key={index} className="story-item pdf-story-item">
                    <PdfSlideshow
                      file={storyItem.pdf}
                      expandLabel={storyItem.pdfExpandLabel}
                      collapseLabel={storyItem.pdfCollapseLabel}
                    />
                  </div>
                );
              }

              const hasMedia = storyItem.image || storyItem.video;

              return (
              <div
                key={index}
                className={`story-item ${
                  hasMedia
                    ? index % 2 === 0
                      ? 'text-left'
                      : 'text-right'
                    : 'story-item-text-only'
                }`}
              >
                <div className="story-content">
                  <div className="story-text">
                    <h3>{storyItem.title}</h3>
                    <p>{storyItem.content}</p>
                    {storyItem.button && (
                      <a 
                        href={storyItem.button.url} 
                        target={storyItem.button.external ? "_blank" : "_self"}
                        rel={storyItem.button.external ? "noopener noreferrer" : ""}
                        className="btn btn-primary"
                      >
                        {storyItem.button.text}
                      </a>
                    )}
                  </div>
                  {hasMedia && (
                  <div className="story-media">
                    {storyItem.image && (
                      <div className="story-image">
                        {Array.isArray(storyItem.image) ? (
                          <div
                            className={`image-gallery ${
                              storyItem.image.length === 3
                                ? 'image-gallery-three'
                                : storyItem.image.length >= 4
                                ? 'image-gallery-square'
                                : ''
                            }`}
                          >
                            {storyItem.image.map((img, imgIndex) => (
                              <img key={imgIndex} src={img} alt={`${storyItem.title} ${imgIndex + 1}`} />
                            ))}
                          </div>
                        ) : (
                          <img src={storyItem.image} alt={storyItem.title} />
                        )}
                      </div>
                    )}
                    {storyItem.video && (
                      <div className="story-video">
                        <video controls>
                          <source src={storyItem.video} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>

          <div className="outcome-section">
            <h3>Outcome</h3>
            <p>{project.outcome}</p>
          </div>

          <div className="final-images-section">
            <h3>Final Images</h3>
            <div className="images-grid-centered">
              {project.finalImages.map((image, index) => (
                <img 
                  key={index} 
                  src={image} 
                  alt={`${project.title} final ${index + 1}`}
                  onClick={() => setSelectedImage(image)}
                  className="clickable-image"
                />
              ))}
            </div>
          </div>

          <div className="return-to-projects">
            <button onClick={returnToProjects} className="btn btn-primary return-btn">
              ← Back to Projects
            </button>
          </div>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div className="image-modal" onClick={() => setSelectedImage(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedImage(null)}>
                ×
              </button>
              <img src={selectedImage} alt="Enlarged view" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
